import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useCustomer } from "../context/CustomerContext";
import PatchApprovalPolicyCard from "../components/PatchApprovalPolicyCard";
import MaintenanceWindowCard from "../components/MaintenanceWindowCard";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import { usePublishScreenData } from "../utils/aiScreenContext";
import {
  ComplianceSummary,
  CustomerCompliance,
  CustomerComplianceDetail,
} from "../types";

type SortKey = keyof Pick<
  CustomerCompliance,
  | "customer_name"
  | "total_devices"
  | "devices_behind"
  | "pending_critical"
  | "pending"
  | "oldest_pending_age_days"
  | "devices_pending_reboot"
>;

type DevRow = NonNullable<CustomerComplianceDetail["devices"]>[number];
type DevFilter =
  | ""
  | "up_to_date"
  | "behind"
  | "optional_only"
  | "reboot_only"
  | "pending_reboot"
  | "pending_critical"
  | "installed";
// Per-device predicates that match the backend's summary counts exactly
// (services/patch_compliance.py): up_to_date = no pending & no reboot;
// behind = has critical/important; etc. "pending_critical" drills to devices
// that have any critical pending (the card value is a critical-update total).
const DEVICE_FILTERS: Record<Exclude<DevFilter, "">, (d: DevRow) => boolean> = {
  up_to_date: (d) => d.pending === 0 && !d.pending_reboot,
  behind: (d) => d.is_behind,
  // The two that make the four add up. Mirrors the backend exactly: behind
  // wins over optional-only, and a device that is behind AND awaiting a
  // restart counts as behind - otherwise a machine lands in two buckets and
  // the tiles stop summing to the device count.
  optional_only: (d) => d.pending > 0 && !d.is_behind,
  reboot_only: (d) => d.pending === 0 && d.pending_reboot,
  pending_reboot: (d) => d.pending_reboot,
  pending_critical: (d) => d.pending_critical > 0,
  installed: (d) => d.installed_last_30d > 0,
};
const FILTER_LABEL: Record<Exclude<DevFilter, "">, string> = {
  up_to_date: "up to date",
  behind: "behind",
  optional_only: "with optional updates only",
  reboot_only: "awaiting only a restart",
  pending_reboot: "pending reboot",
  pending_critical: "with pending critical",
  installed: "installed in last 30d",
};

// Semantic health order (online first) for the by-device Status column sort.
const DEV_STATUS_RANK: Record<string, number> = { online: 0, offline: 1 };

export default function PatchCompliance() {
  const { user } = useAuth();
  // Mirrors require_role("admin", "technician") on the endpoint — showing a
  // button that always 403s is worse than not showing it.
  const canPush = user?.role === "admin" || user?.role === "technician";
  // Respect the global customer selector: null = All customers (aggregate),
  // otherwise scope to the one selected customer. Layout remounts this page
  // when the selection changes, so the fetch below re-runs with the new id.
  const { currentCustomerId, current } = useCustomer();
  const [summary, setSummary] = useState<ComplianceSummary | null>(null);
  const [customers, setCustomers] = useState<CustomerCompliance[]>([]);
  const [detail, setDetail] = useState<CustomerComplianceDetail | null>(null);
  const [sortBy, setSortBy] = useState<SortKey>("pending_critical");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [loading, setLoading] = useState(true);
  const devSort = useSort("pending_critical", "desc");
  const [deviceFilter, setDeviceFilter] = useState<DevFilter>("");

  // Let the global AI Assist answer "what's on my screen".
  //
  // The assistant is mounted in Layout, so it is already on all 76 screens -
  // but it only received the ROUTE. Eleven screens published their rows, so
  // "which of these are worst?" worked on eleven and silently answered from
  // nothing on the other sixty-five. That reads as the assistant being
  // useless rather than uninformed.
  //
  // Trimmed rather than passed whole, for three reasons: the helper's own
  // contract asks for key fields only, the payload is billed per token, and
  // an interface is not assignable to Record<string, unknown> in TypeScript -
  // so a raw pass would not compile anyway.
  // This screen shows TWO different tables depending on the customer
  // selector: per-customer rollups when nothing is selected, and per-DEVICE
  // rows when one customer is. `customers` is emptied in the scoped case, so
  // publishing it unconditionally would have told the assistant the screen
  // was empty on exactly the view a technician is most likely to ask about.
  usePublishScreenData(
    currentCustomerId == null
      ? "Patch compliance by customer"
      : "Patch compliance by device",
    useMemo(
      () =>
        currentCustomerId == null
          ? customers.map((c) => ({
              customer: c.customer_name,
              devices: c.total_devices,
              up_to_date: c.devices_up_to_date,
              behind: c.devices_behind,
              pending_reboot: c.devices_pending_reboot,
              pending_critical: c.pending_critical,
              pending_important: c.pending_important,
              failed_last_30d: c.failed_last_30d,
              oldest_pending_age_days: c.oldest_pending_age_days ?? null,
            }))
          : (detail?.devices ?? []).map((d) => ({
              hostname: d.hostname,
              status: d.device_status,
              behind: d.is_behind,
              pending: d.pending,
              pending_critical: d.pending_critical,
              pending_reboot: d.pending_reboot,
              oldest_pending_age_days: d.oldest_pending_age_days ?? null,
            })),
      [currentCustomerId, customers, detail],
    ),
    currentCustomerId == null
      ? "all customers, one row each"
      : `${current?.name || "one customer"}, one row per device`,
  );

  useEffect(() => {
    setLoading(true);
    if (currentCustomerId == null) {
      Promise.all([
        api.get("/api/v1/patches/compliance/summary"),
        api.get("/api/v1/patches/compliance/customers"),
      ])
        .then(([s, c]) => {
          setSummary(s.data);
          setCustomers(c.data);
          setDetail(null);
        })
        .finally(() => setLoading(false));
    } else {
      loadScoped().finally(() => setLoading(false));
    }
    // loadScoped closes over currentCustomerId, which is the only input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCustomerId]);

  const loadScoped = () =>
    api
      .get(`/api/v1/patches/compliance/customers/${currentCustomerId}`)
      .then((r) => {
        setDetail(r.data);
        setSummary(null);
        setCustomers([]);
      });

  const sorted = useMemo(() => {
    const arr = [...customers];
    arr.sort((a, b) => {
      const av = a[sortBy] ?? -1;
      const bv = b[sortBy] ?? -1;
      if (av === bv) return 0;
      const dir = sortDir === "asc" ? 1 : -1;
      return av > bv ? dir : -dir;
    });
    return arr;
  }, [customers, sortBy, sortDir]);

  // Device rows (scoped view): click-to-sort, defaulting worst-first (most critical).
  // Push-on-demand. Patches otherwise install inside the customer's
  // maintenance window; this is the "Sunday 2am is not soon enough" path.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState<{ ok: boolean; message: string } | null>(null);

  const toggleOne = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const installNow = async (allowReboot: boolean) => {
    setPushing(true);
    setPushResult(null);
    try {
      const r = await api.post("/api/v1/patches/compliance/install", {
        device_ids: [...selected],
        allow_reboot: allowReboot,
      });
      setPushResult({ ok: r.data.ok, message: r.data.message });
      setSelected(new Set());
      loadScoped();
    } catch (e: any) {
      setPushResult({
        ok: false,
        message: e?.response?.data?.detail || "Could not queue the install",
      });
    } finally {
      setPushing(false);
    }
  };

  const sortedDevices = sortRows(detail?.devices ?? [], devSort.sort, (d, key) => {
    switch (key) {
      case "hostname":
        return (d.hostname || "").toLowerCase();
      case "device_status":
        return DEV_STATUS_RANK[d.device_status] ?? 2;
      case "is_behind":
        return d.is_behind ? 1 : 0;
      case "pending":
        return d.pending;
      case "pending_reboot":
        return d.pending_reboot ? 1 : 0;
      case "oldest_pending_age_days":
        return d.oldest_pending_age_days ?? -1;
      default:
        return d.pending_critical;
    }
  });

  const visibleDevices = deviceFilter
    ? sortedDevices.filter(DEVICE_FILTERS[deviceFilter])
    : sortedDevices;
  const toggleFilter = (f: Exclude<DevFilter, "">) =>
    setDeviceFilter((cur) => (cur === f ? "" : f));

  const toggleSort = (k: SortKey) => {
    if (sortBy === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortBy(k);
      setSortDir("desc");
    }
  };

  if (loading) return <div className="text-gray-400 p-8">Loading...</div>;

  const scoped = currentCustomerId != null;
  // Unified counts for the stat cards, sourced from the aggregate summary
  // ("All customers") or the single-customer detail (when scoped).
  const stats = summary ?? detail;
  const devicesSub = scoped
    ? undefined
    : summary
      ? `${summary.total_customers} customers`
      : undefined;
  const scopeName = current?.name;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">
        Patch Compliance{scoped && scopeName ? ` — ${scopeName}` : ""}
      </h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Read-only snapshot of Windows Update status{" "}
        {scoped ? "for the selected customer" : "across all customers"}. Declined patches don't
        count as a gap.
      </p>

      {scoped && currentCustomerId != null && (
        <MaintenanceWindowCard
          customerId={currentCustomerId}
          canEdit={user?.role === "admin" || user?.role === "technician"}
        />
      )}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
          <StatCard
            label="Devices"
            value={stats.total_devices}
            sub={devicesSub}
            onClick={scoped ? () => setDeviceFilter("") : undefined}
            active={scoped && deviceFilter === ""}
          />
          <StatCard
            label="Up to date"
            value={stats.devices_up_to_date}
            color="text-green-700 dark:text-green-400"
            onClick={scoped ? () => toggleFilter("up_to_date") : undefined}
            active={deviceFilter === "up_to_date"}
          />
          <StatCard
            label="Behind"
            value={stats.devices_behind}
            color="text-orange-600 dark:text-orange-400"
            onClick={scoped ? () => toggleFilter("behind") : undefined}
            active={deviceFilter === "behind"}
          />
          {/* THE TWO TILES THAT MAKE THE COUNTS ADD UP.
              "Up to date" and "Behind" looked like a partition of the fleet
              and were not: Behind means critical or important is pending, and
              Up to date means nothing pending AND no restart owed. So a
              machine with only optional updates, and one that is fully
              patched but waiting to reboot, appeared in NEITHER. On DCNS's
              six devices that was four machines with no tile — the reader
              could not tell whether they were fine or unmeasured.
              Up to date + Behind + Optional only + Reboot only == Devices. */}
          <StatCard
            label="Optional only"
            value={stats.devices_optional_only}
            color="text-gray-600 dark:text-gray-300"
            onClick={scoped ? () => toggleFilter("optional_only") : undefined}
            active={deviceFilter === "optional_only"}
          />
          <StatCard
            label="Reboot only"
            value={stats.devices_reboot_only}
            color="text-gray-600 dark:text-gray-300"
            onClick={scoped ? () => toggleFilter("reboot_only") : undefined}
            active={deviceFilter === "reboot_only"}
          />
          {/* Counts EVERY device owing a restart, including ones already
              counted as Behind — so it deliberately overlaps the four above
              and must not be added to them. */}
          <StatCard
            label="Pending reboot"
            value={stats.devices_pending_reboot}
            color="text-orange-600 dark:text-orange-400"
            onClick={scoped ? () => toggleFilter("pending_reboot") : undefined}
            active={deviceFilter === "pending_reboot"}
          />
          <StatCard
            label="Pending critical"
            value={stats.pending_critical}
            color="text-red-600 dark:text-red-400"
            onClick={scoped ? () => toggleFilter("pending_critical") : undefined}
            active={deviceFilter === "pending_critical"}
          />
          <StatCard
            label="Installed (30d)"
            value={stats.installed_last_30d}
            color="text-blue-600 dark:text-blue-400"
            onClick={scoped ? () => toggleFilter("installed") : undefined}
            active={deviceFilter === "installed"}
          />
        </div>
      )}

      {scoped ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
          <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              By device{deviceFilter ? ` — ${FILTER_LABEL[deviceFilter]}` : ""}
            </h2>
            {canPush && selected.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {selected.size} selected
                </span>
                <button
                  type="button"
                  disabled={pushing}
                  onClick={() => {
                    if (
                      window.confirm(
                        `Install all available updates on ${selected.size} device(s) now?\n\n` +
                          "The machines will NOT be rebooted — updates that need one will sit " +
                          "pending until the next reboot.\n\n" +
                          "Devices that are offline will be skipped rather than queued, so " +
                          "nothing starts unexpectedly later."
                      )
                    )
                      installNow(false);
                  }}
                  className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white font-medium disabled:opacity-50"
                >
                  {pushing ? "Queueing…" : "Install now"}
                </button>
                <button
                  type="button"
                  disabled={pushing}
                  onClick={() => {
                    if (
                      window.confirm(
                        `Install updates AND allow a reboot on ${selected.size} device(s)?\n\n` +
                          "Anyone signed in gets a warning and five minutes to save their work " +
                          "before the machine restarts."
                      )
                    )
                      installNow(true);
                  }}
                  className="text-xs px-3 py-1.5 rounded-lg border border-orange-300 dark:border-orange-800 text-orange-700 dark:text-orange-400 font-medium disabled:opacity-50"
                >
                  Install + reboot
                </button>
                <button
                  type="button"
                  onClick={() => setSelected(new Set())}
                  className="text-xs px-2 py-1.5 text-gray-500 dark:text-gray-400 hover:underline"
                >
                  Clear
                </button>
              </div>
            )}
            <span className="text-xs text-gray-400">
              {deviceFilter
                ? `${visibleDevices.length} ${FILTER_LABEL[deviceFilter]}`
                : (detail?.devices.length ?? 0)}
            </span>
          </div>
          {pushResult && (
            // Says what did NOT happen as well as what did — a bulk push that
            // silently skips half its targets is the failure worth surfacing.
            <div
              className={`px-4 py-2 text-xs border-b border-gray-100 dark:border-gray-700 ${
                pushResult.ok
                  ? "bg-green-50 dark:bg-green-900/25 text-green-700 dark:text-green-300"
                  : "bg-amber-50 dark:bg-amber-900/25 text-amber-800 dark:text-amber-300"
              }`}
            >
              {pushResult.message}
            </div>
          )}
          {!detail || visibleDevices.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">
              {deviceFilter
                ? `No devices ${FILTER_LABEL[deviceFilter]}.`
                : "No devices reporting for this customer yet."}
            </p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 uppercase">
                <tr>
                  {canPush && (
                    <th className="px-3 py-2 w-8">
                      <input
                        type="checkbox"
                        aria-label="Select all shown"
                        checked={visibleDevices.length > 0 && selected.size === visibleDevices.length}
                        onChange={(e) =>
                          setSelected(
                            e.target.checked
                              ? new Set(visibleDevices.map((d) => d.device_id))
                              : new Set(),
                          )
                        }
                      />
                    </th>
                  )}
                  <SortableTh label="Device" sortKey="hostname" sort={devSort.sort} onSort={devSort.toggle} />
                  <SortableTh label="Status" sortKey="device_status" sort={devSort.sort} onSort={devSort.toggle} hide="hidden md:table-cell" />
                  <SortableTh label="Behind" sortKey="is_behind" sort={devSort.sort} onSort={devSort.toggle} align="right" hide="hidden sm:table-cell" />
                  <SortableTh label="Critical" sortKey="pending_critical" sort={devSort.sort} onSort={devSort.toggle} align="right" />
                  <SortableTh label="All pending" sortKey="pending" sort={devSort.sort} onSort={devSort.toggle} align="right" hide="hidden sm:table-cell" />
                  <SortableTh label="Reboot" sortKey="pending_reboot" sort={devSort.sort} onSort={devSort.toggle} align="right" hide="hidden lg:table-cell" />
                  <SortableTh label="Oldest (days)" sortKey="oldest_pending_age_days" sort={devSort.sort} onSort={devSort.toggle} align="right" hide="hidden lg:table-cell" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {visibleDevices.map((d) => (
                  <tr key={d.device_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    {canPush && (
                      <td className="px-3 py-2.5">
                        <input
                          type="checkbox"
                          aria-label={`Select ${d.hostname}`}
                          checked={selected.has(d.device_id)}
                          onChange={() => toggleOne(d.device_id)}
                        />
                      </td>
                    )}
                    <td className="px-4 py-2.5">
                      <Link
                        to={`/devices/${d.device_id}`}
                        className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                      >
                        {d.hostname}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 hidden md:table-cell">
                      <span
                        className={
                          d.device_status === "online" ? "text-green-700 dark:text-green-400" : "text-gray-400"
                        }
                      >
                        {d.device_status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right hidden sm:table-cell">
                      {d.is_behind ? (
                        <span className="text-orange-700 dark:text-orange-400 font-medium">Yes</span>
                      ) : (
                        <span className="text-gray-400">No</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {d.pending_critical > 0 ? (
                        <span className="text-red-700 dark:text-red-400 font-bold">{d.pending_critical}</span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-300 hidden sm:table-cell">{d.pending}</td>
                    <td className="px-4 py-2.5 text-right hidden lg:table-cell">
                      {d.pending_reboot ? (
                        <span className="text-orange-600 dark:text-orange-400">Yes</span>
                      ) : (
                        <span className="text-gray-400">No</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-300 hidden lg:table-cell">
                      {d.oldest_pending_age_days != null ? `${d.oldest_pending_age_days}d` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
          <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">By customer</h2>
            <span className="text-xs text-gray-400">{customers.length}</span>
          </div>
          {customers.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No customers yet.</p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 uppercase">
                <tr>
                  <Th
                    onClick={() => toggleSort("customer_name")}
                    active={sortBy === "customer_name"}
                    dir={sortDir}
                  >
                    Customer
                  </Th>
                  <Th
                    onClick={() => toggleSort("total_devices")}
                    active={sortBy === "total_devices"}
                    dir={sortDir}
                    align="right"
                  >
                    Devices
                  </Th>
                  <Th className="hidden md:table-cell"
                    onClick={() => toggleSort("devices_behind")}
                    active={sortBy === "devices_behind"}
                    dir={sortDir}
                    align="right"
                  >
                    Behind
                  </Th>
                  <Th
                    onClick={() => toggleSort("pending_critical")}
                    active={sortBy === "pending_critical"}
                    dir={sortDir}
                    align="right"
                  >
                    Critical
                  </Th>
                  <Th className="hidden md:table-cell"
                    onClick={() => toggleSort("pending")}
                    active={sortBy === "pending"}
                    dir={sortDir}
                    align="right"
                  >
                    All pending
                  </Th>
                  <Th className="hidden lg:table-cell"
                    onClick={() => toggleSort("devices_pending_reboot")}
                    active={sortBy === "devices_pending_reboot"}
                    dir={sortDir}
                    align="right"
                  >
                    Reboot
                  </Th>
                  <Th className="hidden lg:table-cell"
                    onClick={() => toggleSort("oldest_pending_age_days")}
                    active={sortBy === "oldest_pending_age_days"}
                    dir={sortDir}
                    align="right"
                  >
                    Oldest (days)
                  </Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {sorted.map((c) => (
                  <tr key={c.customer_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <td className="px-4 py-2.5">
                      <Link
                        to={`/customers/${c.customer_id}`}
                        className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                      >
                        {c.customer_name}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-300">
                      {c.devices_up_to_date}/{c.total_devices}
                    </td>
                    <td className="px-4 py-2.5 text-right hidden md:table-cell">
                      {c.devices_behind > 0 ? (
                        <span className="text-orange-700 dark:text-orange-400 font-medium">{c.devices_behind}</span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {c.pending_critical > 0 ? (
                        <span className="text-red-700 dark:text-red-400 font-bold">{c.pending_critical}</span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-300 hidden md:table-cell">{c.pending}</td>
                    <td className="px-4 py-2.5 text-right hidden lg:table-cell">
                      {c.devices_pending_reboot > 0 ? (
                        <span className="text-orange-600 dark:text-orange-400">{c.devices_pending_reboot}</span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-300 hidden lg:table-cell">
                      {c.oldest_pending_age_days != null ? `${c.oldest_pending_age_days}d` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {user?.role === "admin" && (
        <div className="mt-6">
          <PatchApprovalPolicyCard />
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  color,
  onClick,
  active,
}: {
  label: string;
  value: number;
  sub?: string;
  color?: string;
  onClick?: () => void;
  active?: boolean;
}) {
  const base = `bg-white dark:bg-gray-800 rounded-xl border shadow-sm p-4 text-left w-full ${
    active
      ? "border-orange-400 ring-2 ring-orange-300 dark:ring-orange-500/40"
      : "border-gray-100 dark:border-gray-700"
  }`;
  const inner = (
    <>
      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-bold mt-0.5 ${color || "text-gray-900 dark:text-gray-100"}`}>{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </>
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`${base} cursor-pointer hover:shadow-md transition-shadow`}>
        {inner}
      </button>
    );
  }
  return <div className={base}>{inner}</div>;
}

function Th({
  children,
  onClick,
  active,
  dir,
  align,
  className = "",
}: {
  children: React.ReactNode;
  onClick: () => void;
  active: boolean;
  dir: "asc" | "desc";
  align?: "right";
  /** Responsive `hidden md:table-cell` etc. MUST match the column's `<td>`. */
  className?: string;
}) {
  return (
    <th
      onClick={onClick}
      className={`px-4 py-2 cursor-pointer select-none ${align === "right" ? "text-right" : "text-left"} ${active ? "text-blue-600 dark:text-blue-400" : ""} ${className}`}
    >
      {children}
      {active && <span className="ml-1">{dir === "asc" ? "▲" : "▼"}</span>}
    </th>
  );
}
